import { useEffect, useMemo, useRef, useState } from "react";

import { useT } from "../i18n/LanguageProvider";
import { searchInstitutions } from "../api/institutions";
import { searchOrganisations } from "../api/organisations";
import { C, font } from "../styles/tokens";

/**
 * VerifierPicker - search-with-autocomplete picker for institutions and
 * organisations.
 *
 * Behavior:
 *   - Search box at the top filters live as the user types (debounced 200ms)
 *   - Below the search, a scrollable list of matching verifiers as cards
 *   - Each card: name, location, registration number, hover state, click selects
 *   - Selected verifier shows above the search with a "Change" button
 *   - Recent verifiers section appears when search is empty (Phase 2 - for now
 *     it just shows the first 6 verifiers alphabetically as "Suggested")
 *
 * Props:
 *   verifierType: "INSTITUTION" | "ORGANISATION"
 *   value: { id, name, ... } | null
 *   onChange: (verifier) => void
 */
export function VerifierPicker({ verifierType, value, onChange }) {
  const t = useT();
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Debounce the search query
  useEffect(() => {
    const handle = setTimeout(() => setDebouncedQuery(query), 200);
    return () => clearTimeout(handle);
  }, [query]);

  // Fetch results when type or debounced query changes
  useEffect(() => {
    if (value) return; // Don't search while a value is selected
    setLoading(true);
    setError(null);
    const fetcher =
      verifierType === "INSTITUTION" ? searchInstitutions : searchOrganisations;
    fetcher(debouncedQuery, 30)
      .then(setResults)
      .catch((err) => setError(err.message ?? "Search failed"))
      .finally(() => setLoading(false));
  }, [verifierType, debouncedQuery, value]);

  // When the user changes the verifierType while a verifier is selected, clear it
  // (the selected verifier is from a different table).
  const lastTypeRef = useRef(verifierType);
  useEffect(() => {
    if (lastTypeRef.current !== verifierType) {
      lastTypeRef.current = verifierType;
      if (value) onChange(null);
    }
  }, [verifierType, value, onChange]);

  // ---- Selected state ----
  if (value) {
    return (
      <div style={selectedCardStyle}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: C.green,
              textTransform: "uppercase",
              letterSpacing: 0.4,
              marginBottom: 4,
            }}
          >
            {verifierType === "INSTITUTION"
              ? t("verify.academicInstitution")
              : t("verify.organisation")}
          </div>
          <div style={{ fontSize: 15, fontWeight: 700, color: C.textDark }}>
            {value.name}
          </div>
          <div style={{ fontSize: 12, color: C.textMed, marginTop: 2 }}>
            {value.city && `${value.city}, ${value.state} - `}
            <span style={{ fontFamily: "monospace", fontSize: 11 }}>
              {value.registrationNumber}
            </span>
          </div>
        </div>
        <button
          onClick={() => onChange(null)}
          type="button"
          style={changeButtonStyle}
        >
          {t("common.edit")}
        </button>
      </div>
    );
  }

  // ---- Picker state ----
  return (
    <div>
      <div style={{ position: "relative", marginBottom: 8 }}>
        <SearchIcon />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t("verify.searchInstitution")}
          autoFocus
          style={searchInputStyle}
        />
      </div>

      <div style={listContainerStyle}>
        {error && (
          <div style={{ padding: 16, fontSize: 13, color: C.red }}>{error}</div>
        )}
        {loading && (
          <div style={{ padding: 16, fontSize: 13, color: C.textMed }}>
            {t("common.loading")}
          </div>
        )}
        {!loading && results.length === 0 && (
          <div style={{ padding: 24, textAlign: "center" }}>
            <div style={{ fontSize: 13, color: C.textMed }}>
              {query
                ? t("verify.noResultsFound")
                : t("verify.noVerifiersAvailable")}
            </div>
          </div>
        )}
        {!loading && results.length > 0 && (
          <>
            {!query && <div style={listHeaderStyle}>Suggested</div>}
            {results.map((v) => (
              <ResultCard
                key={v.id}
                verifier={v}
                onSelect={() => onChange(v)}
              />
            ))}
          </>
        )}
      </div>
    </div>
  );
}

function ResultCard({ verifier, onSelect }) {
  const [hover, setHover] = useState(false);
  return (
    <button
      onClick={onSelect}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      type="button"
      style={{
        ...resultCardStyle,
        background: hover ? C.blueLight : "transparent",
        borderLeft: hover ? `3px solid ${C.blue}` : "3px solid transparent",
      }}
    >
      <div style={{ flex: 1, minWidth: 0, textAlign: "left" }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: C.textDark }}>
          {verifier.name}
        </div>
        <div style={{ fontSize: 12, color: C.textMed, marginTop: 2 }}>
          {verifier.city && `${verifier.city}, ${verifier.state}`}
        </div>
        <div
          style={{
            fontSize: 11,
            color: C.textLight,
            marginTop: 2,
            fontFamily: "monospace",
          }}
        >
          {verifier.registrationNumber}
        </div>
      </div>
      <div
        style={{
          flexShrink: 0,
          color: hover ? C.blue : C.textLight,
          fontSize: 12,
          fontWeight: 600,
        }}
      >
        Select
      </div>
    </button>
  );
}

function SearchIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke={C.textLight}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{
        position: "absolute",
        left: 12,
        top: "50%",
        transform: "translateY(-50%)",
      }}
    >
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}

const searchInputStyle = {
  width: "100%",
  padding: "10px 12px 10px 36px",
  fontSize: 14,
  fontFamily: font,
  border: `1.5px solid ${C.dividerStrong}`,
  borderRadius: 6,
  outline: "none",
  boxSizing: "border-box",
  background: C.bg,
};

const listContainerStyle = {
  border: `1px solid ${C.divider}`,
  borderRadius: 6,
  maxHeight: 320,
  overflowY: "auto",
  background: C.bg,
};

const listHeaderStyle = {
  padding: "8px 14px",
  fontSize: 10,
  fontWeight: 700,
  color: C.textLight,
  textTransform: "uppercase",
  letterSpacing: 0.5,
  borderBottom: `1px solid ${C.divider}`,
  background: C.bgMuted,
};

const resultCardStyle = {
  width: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 12,
  padding: "12px 14px",
  border: 0,
  borderBottom: `1px solid ${C.divider}`,
  cursor: "pointer",
  fontFamily: font,
  transition: "background 0.12s ease, border-left 0.12s ease",
};

const selectedCardStyle = {
  display: "flex",
  alignItems: "center",
  gap: 12,
  padding: "14px 16px",
  background: C.greenLight + "55",
  border: `1.5px solid ${C.green + "55"}`,
  borderRadius: 8,
};

const changeButtonStyle = {
  flexShrink: 0,
  padding: "6px 14px",
  fontSize: 12,
  fontWeight: 600,
  color: C.green,
  background: "transparent",
  border: `1px solid ${C.green}55`,
  borderRadius: 4,
  cursor: "pointer",
  fontFamily: font,
};
